import { Skeleton, Stack } from "@altimateai/lego";

export type ChartSkeletonVariant = "bar" | "line";

interface ChartSkeletonProps {
  /** Total height of the plot + axis labels area, in pixels. Default 320. */
  height?: number;
  /** "bar" renders bars of varying height; "line" renders an SVG zigzag. */
  variant?: ChartSkeletonVariant;
  /** Number of bars or line points. Default 12. */
  pointCount?: number;
  /** Number of horizontal gridlines. Default 4. */
  gridlines?: number;
  /** Render a legend row beneath the plot. */
  showLegend?: boolean;
  /** Number of legend chips. Default 2. */
  legendCount?: number;
  /** aria-label for the wrapper. */
  ariaLabel?: string;
  className?: string;
  /** Appended to each bar's className in the "bar" variant, for callers that
   *  need more contrast than the default `Skeleton` background provides. The
   *  "line" variant has no `Skeleton` elements (it draws an SVG polyline), so
   *  this has no effect there. */
  barClassName?: string;
}

const BAR_HEIGHTS = [60, 35, 80, 50, 70, 45, 90, 55, 75, 40, 65, 50, 78, 42, 88, 58];

const ChartSkeleton = ({
  height = 320,
  variant = "bar",
  pointCount = 12,
  gridlines = 4,
  showLegend = false,
  legendCount = 2,
  ariaLabel = "Loading chart",
  className = "",
  barClassName = "",
}: ChartSkeletonProps) => {
  const heights = Array.from({ length: pointCount }, (_, i) => BAR_HEIGHTS[i % BAR_HEIGHTS.length]);

  return (
    <div aria-label={ariaLabel} className={`al-w-full ${className}`} style={{ height }}>
      <Stack direction="row" className="al-h-[calc(100%-32px)]">
        {/* Y-axis tick labels */}
        <Stack direction="column" justify="between" className="al-pr-2 al-py-1 al-w-10">
          {Array.from({ length: gridlines + 1 }).map((_, i) => (
            <Skeleton key={i} className="al-h-2 al-w-8" />
          ))}
        </Stack>

        {/* Plot area */}
        <div className="al-relative al-flex-1 al-border-l al-border-b al-border-gray-200">
          {/* Horizontal gridlines */}
          {Array.from({ length: gridlines }).map((_, i) => (
            <div
              key={i}
              className="al-absolute al-left-0 al-right-0 al-border-t al-border-dashed al-border-gray-100"
              style={{ top: `${((i + 1) / (gridlines + 1)) * 100}%` }}
            />
          ))}

          {variant === "bar" ? (
            <Stack
              direction="row"
              align="end"
              justify="around"
              className="al-absolute al-inset-0 al-px-2"
            >
              {heights.map((h, i) => (
                <Skeleton
                  key={i}
                  className={`al-rounded-t-sm ${barClassName}`}
                  style={{ height: `${h}%`, width: `${Math.max(40 / pointCount, 2)}%` }}
                />
              ))}
            </Stack>
          ) : (
            <svg
              className="al-absolute al-inset-0 al-w-full al-h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polyline
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
                className="al-text-gray-200 al-animate-pulse"
                points={heights
                  .map((h, i) => `${(i / (heights.length - 1)) * 100},${100 - h * 0.9}`)
                  .join(" ")}
              />
            </svg>
          )}
        </div>
      </Stack>

      {/* X-axis tick labels */}
      <Stack direction="row" justify="between" gap="xs" className="al-pl-10 al-pt-1 al-h-6">
        {Array.from({ length: Math.min(pointCount, 8) }).map((_, i) => (
          <Skeleton key={i} className="al-h-2 al-w-8" />
        ))}
      </Stack>

      {/* Legend */}
      {showLegend && (
        <Stack direction="row" justify="center" gap="md" className="al-pt-2">
          {Array.from({ length: legendCount }).map((_, i) => (
            <Stack key={i} direction="row" align="center" gap="xs">
              <Skeleton className="al-h-3 al-w-3 al-rounded-sm" />
              <Skeleton className="al-h-2 al-w-16" />
            </Stack>
          ))}
        </Stack>
      )}
    </div>
  );
};

export default ChartSkeleton;
